import React, { useState, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import GameKeypad from './GameKeypad';

function NumberPickerVault({ game, selectedNumbers, onSelect, onRemove, blockedPickNumbers, maxSelectableNumbers }) {
  const blocked = blockedPickNumbers ?? new Set(selectedNumbers);
  const maxPicks = maxSelectableNumbers ?? game.maxNumbersPerTicket;
  const [inputBuffer, setInputBuffer] = useState('');
  const clearTimeoutRef = useRef(null);
  const maxDigits = 2;

  const clearDisplay = () => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setInputBuffer('');
  };

  const handleDigit = (digit) => {
    // Clear any pending display clear when starting new input
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }

    if (inputBuffer.length < maxDigits) {
      const newBuffer = inputBuffer + digit;
      setInputBuffer(newBuffer);

      if (newBuffer.length === maxDigits) {
        const num = parseInt(newBuffer, 10);
        if (num >= 0 && num <= 99) {
          if (blocked.has(num)) {
            toast.error(`Number ${String(num).padStart(2, '0')} already selected`);
            clearDisplay();
          } else if (selectedNumbers.length < maxPicks) {
            onSelect(num);
            // Keep the full number displayed for 500ms before clearing
            clearTimeoutRef.current = setTimeout(() => {
              setInputBuffer('');
              clearTimeoutRef.current = null;
            }, 500);
          } else {
            toast.error(`Maximum ${maxPicks} numbers allowed this round`);
            clearDisplay();
          }
        } else {
          clearDisplay();
        }
      }
    }
  };

  const handleBackspace = () => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setInputBuffer((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    clearDisplay();
  };

  const keypadDisabled = useMemo(() => {
    const disabled = [];
    if (selectedNumbers.length >= maxPicks) {
      for (let d = 0; d <= 9; d += 1) disabled.push(String(d));
      return disabled;
    }
    if (inputBuffer.length === maxDigits - 1) {
      const { min, max } = game.numberRange;
      for (let d = 0; d <= 9; d += 1) {
        const num = parseInt(inputBuffer + String(d), 10);
        if (num >= min && num <= max && blocked.has(num)) disabled.push(String(d));
      }
    }
    return disabled;
  }, [inputBuffer, selectedNumbers.length, game, blocked, maxPicks]);

  return (
    <div className="space-y-4">
      {/* Display */}
      <div className="flex items-center justify-center">
        <div className="w-full max-w-xs">
          <div className="dark-input text-center text-2xl font-mono font-bold tracking-widest py-4 min-h-[60px] flex items-center justify-center">
            {inputBuffer.padEnd(maxDigits, '_') || '__'}
          </div>
        </div>
      </div>

      {/* Keypad */}
      <div className="max-w-xs mx-auto">
        <GameKeypad
          onDigit={handleDigit}
          onBackspace={handleBackspace}
          onClear={handleClear}
          disabledButtons={keypadDisabled}
        />
      </div>
    </div>
  );
}

export default NumberPickerVault;
